import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowRight, Zap, Shield, Users, TrendingUp } from 'lucide-react';
import { ResultCard } from '../components/ResultCard';
import RecentDecodes, { saveRecentDecode } from '../components/RecentDecodes';
import { decodeSingle, formatKenmerk } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';

const HomePage = () => {
  useSEO(); // Apply homepage SEO
  
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [recentKey, setRecentKey] = useState(0); // To force refresh of RecentDecodes
  const { t, language } = useLanguage();

  // Detect if input looks like aanslagnummer (contains letter) or betalingskenmerk (only digits)
  const isAanslagnummer = (value) => {
    const cleaned = value.replace(/[\s\-\.]/g, '').toUpperCase();
    return /[A-Z]/.test(cleaned);
  };

  const handleInputChange = (e) => {
    let value = e.target.value;
    
    // Check if it contains letters (aanslagnummer)
    if (isAanslagnummer(value)) {
      // For aanslagnummers, just clean but don't format with spaces
      setInput(value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
    } else {
      // For betalingskenmerken, format with spaces
      const formatted = formatKenmerk(value);
      setInput(formatted);
    }
  };

  const handleDecode = async (e) => {
    e.preventDefault();
    const cleaned = input.replace(/[\s\-\.]/g, '');
    
    // Validate: either 16 digits (betalingskenmerk) or contains letter at position ~10 (aanslagnummer)
    const hasLetter = /[A-Za-z]/.test(cleaned);
    
    if (!hasLetter && cleaned.length !== 16) {
      setError(language === 'nl' 
        ? 'Voer 16 cijfers (betalingskenmerk) of een aanslagnummer in' 
        : 'Enter 16 digits (payment reference) or an assessment number');
      return;
    }
    
    if (hasLetter && cleaned.length < 10) {
      setError(language === 'nl' 
        ? 'Aanslagnummer te kort' 
        : 'Assessment number too short');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const data = await decodeSingle(cleaned, language);
      setResult(data);
      
      // Save to recent decodes if valid
      if (data.valid) {
        saveRecentDecode(data);
        setRecentKey(prev => prev + 1); // Trigger refresh
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('common_error'));
    } finally {
      setLoading(false);
    }
  };

  // Handle selecting a recent decode
  const handleSelectRecent = async (kenmerk) => {
    const hasLetter = /[A-Za-z]/.test(kenmerk);
    if (hasLetter) {
      setInput(kenmerk.toUpperCase());
    } else {
      const formatted = formatKenmerk(kenmerk);
      setInput(formatted);
    }
    setResult(null);
    setError(null);
    
    // Auto-decode
    setLoading(true);
    try {
      const data = await decodeSingle(kenmerk, language);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || t('common_error'));
    } finally {
      setLoading(false);
    }
  };

  // Check if input is valid for submission
  const isInputValid = () => {
    const cleaned = input.replace(/[\s\-\.]/g, '');
    const hasLetter = /[A-Za-z]/.test(cleaned);
    if (hasLetter) {
      return cleaned.length >= 10;
    }
    return cleaned.length === 16;
  };

  const handleCopy = async () => {
    if (!result) return;
    
    let text;
    if (result.valid) {
      // Get tax name
      const taxName = result.belastingtype?.nl || 'Belasting';
      
      // Get period
      let period = result.tijdvak || '';
      if (period && !period.startsWith('Q') && period !== '-' && period.length > 3) {
        period = period.substring(0, 3).toLowerCase();
      }
      
      // Build: "BTW jan 2026"
      const parts = [];
      parts.push(taxName);
      if (period && period !== '-') {
        parts.push(period);
      }
      if (result.jaar) {
        parts.push(result.jaar);
      }
      text = parts.join(' ');
    } else {
      text = result.kenmerk;
    }
    
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const features = [
    { icon: Zap, title: language === 'nl' ? 'Direct Resultaat' : 'Instant Results', desc: language === 'nl' ? 'Decodeer in milliseconden' : 'Decode in milliseconds' },
    { icon: Shield, title: language === 'nl' ? 'Privacy First' : 'Privacy First', desc: language === 'nl' ? 'Geen opslag van gegevens' : 'No data storage' },
    { icon: Users, title: language === 'nl' ? 'Voor Professionals' : 'For Professionals', desc: language === 'nl' ? 'Gebouwd voor boekhouders' : 'Built for accountants' },
    { icon: TrendingUp, title: language === 'nl' ? 'Bulk Support' : 'Bulk Support', desc: language === 'nl' ? 'Meerdere kenmerken tegelijk' : 'Multiple references at once' },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-background-dark" />
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            {language === 'nl' ? 'Gratis te gebruiken' : 'Free to use'}
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-primary dark:text-white mb-6 tracking-tight"
          >
            {t('hero_title')}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-slate-600 dark:text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed"
          >
            {t('hero_subtitle')}
          </motion.p>

          {/* Decoder Input */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            onSubmit={handleDecode}
            className="max-w-2xl mx-auto"
          >
            {/* Input Field */}
            <div className="relative">
              <input
                type="text"
                value={input}
                onChange={handleInputChange}
                placeholder={language === 'nl' ? '1234 5678 9012 3456 of 123456789L01234' : '1234 5678 9012 3456 or 123456789L01234'}
                className="decoder-input w-full text-center text-2xl md:text-3xl p-6 pr-6 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:border-accent focus:ring-4 focus:ring-accent/10 shadow-lg font-mono tracking-wider placeholder:text-slate-300 dark:placeholder:text-slate-600 placeholder:text-lg"
                maxLength={25}
                data-testid="decoder-input"
              />
            </div>
            
            {/* Decode Button - Below Input */}
            <div className="mt-4 flex justify-center">
              <button
                type="submit"
                disabled={loading || !isInputValid()}
                className="flex items-center gap-3 px-8 py-4 rounded-xl bg-accent text-white font-semibold text-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl btn-press"
                data-testid="decode-btn"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Search className="w-6 h-6" />
                    <span>{t('hero_button')}</span>
                  </>
                )}
              </button>
            </div>
            
            {/* Example */}
            <p className="text-sm text-slate-400 mt-4">
              {language === 'nl' 
                ? 'Voorbeeld: 4865 5274 6150 1300 (betalingskenmerk) of 036000012L0123110 (aanslagnummer)'
                : 'Example: 4865 5274 6150 1300 (payment reference) or 036000012L0123110 (assessment number)'}
            </p>

            {/* Error */}
            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-error text-sm mt-4"
                data-testid="decode-error"
              >
                {error}
              </motion.p>
            )}
          </motion.form>

          {/* Result */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 text-left"
            >
              <ResultCard result={result} onCopy={handleCopy} copied={copied} />
            </motion.div>
          )}

          {/* Recent Decodes */}
          <RecentDecodes 
            key={recentKey} 
            onSelect={handleSelectRecent} 
            language={language} 
          />
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-white dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group p-6 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-12 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-900 text-white"
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl mb-4">
              {language === 'nl' ? 'Meer nodig? Upgrade naar Pro' : 'Need more? Upgrade to Pro'}
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              {language === 'nl' 
                ? 'Bulk decoderen, CSV upload, Excel export en meer voor boekhouders en accountants.'
                : 'Bulk decoding, CSV upload, Excel export and more for bookkeepers and accountants.'}
            </p>
            <a
              href="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors btn-press"
              data-testid="cta-pricing-btn"
            >
              {language === 'nl' ? 'Bekijk prijzen' : 'View pricing'}
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
